import { ArgValidator, Logger, Utils, ApiClientCommunication } from "neo-common";
import { ClientHost, Communication, GameEngine, Platforms } from "neo-games";
import { Controller } from "../GameComponents/Controller";
import { UserControls } from "../Channels/Common/UserControls/UserControls";
import { GameClient } from "neo-games/Engine/Communication/Game/GameClient";
import { AppContext } from "../../Common/AppContext";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { GameData } from "../Channels/Common/Datas/GameData";
import { BetManager } from "./BetManager";
import { BalanceManager } from "./BalanceManager";
import { ModuleManager } from "./ModuleManager";
import { DataManager } from "./DataManager";
import { Languages } from "../Utils/Languages";
import { DialogOpenData } from "../Channels/Common/Dialogs/Dialog";
import { GameApp } from "../GameApp";
import { ResponsibleDialog1 } from "../Channels/Common/Dialogs/ResponsibleDialog1";
import { GameModuleFreespinResponse } from "neo-games/Engine/Communication/Game/Responses/GameModuleFreespinResponse";
import { Game } from "neo-games/Engine/Communication/index";
import { MessageManager } from "./MessageManager";
import { SimilarGames } from "../Channels/Common/SimilarGames/SimilarGames";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { LimitDialog } from "../Channels/Common/Dialogs/LimitDialog";

import { Jackpot } from "../Channels/Common/Bonuses/Jackpot";
import { Scaling } from "../Utils/Scaling";
import { NotificationManager } from "./NotificationManager";
import { Paytable } from "../Channels/Common/Paytable/Paytable";

import { SessionTimer } from "../Channels/Common/Platforms/Playtech/SessionTimer";
import { StageController } from "../Components/StageController";
import { RetentionPanel } from "../Utils/RetentionPanel";

type InitResponse = Communication.Game.InitResponse;
type GameResponse = Communication.Game.GameResponse;
type GameModuleBaseResponse = Communication.Game.GameModuleBaseResponse;
type GameModuleGambleResponse = Communication.Game.GameModuleGambleResponse;
type GameModuleFreespinsResponse = Communication.Game.GameModuleFreespinResponse;
type GameModulePickResponse = Communication.Game.GameModulePickResponse;
type FreeRoundsOffersRequest = Communication.Game.FreeRoundsOffersRequest;
var GameModules = Communication.Game.GameModules;

export class GameManager {
    private static _app: GameEngine.IApp;
    private static _gameClient: GameClient;
    private static _collectModule: string = GameModules.Base;

    static gameResponse: GameResponse;
    static baseResponse: GameModuleBaseResponse;
    static gambleResponse: GameModuleGambleResponse;
    static freespinsResponse: GameModuleFreespinsResponse;
    static pickResponse: GameModulePickResponse;

    static requestSend: boolean = false;

    static exitOnCollect: boolean = false;

    static disableOnCollect: boolean = false;
    static disableGamePlay: boolean = false;

    static dontEnableGamePlay: boolean = false;

    public static get gameClient(): Communication.Game.GameClient {
        return GameManager._gameClient;
    }

    static startGame(gameClient: Communication.Game.GameClient, app: GameEngine.IApp): void {
        if (DEBUG) {
            GameManager.log("startGame");
        }

        ArgValidator.ensureIsNotNullOrUndefined(gameClient, "gameClient");
        GameManager._gameClient = gameClient;
        GameManager._gameClient.onErrorResponse = GameManager.errorReceived;
        DataManager.BackSoundsPlayingFromGame =
            AppContext.current.isBackgroundMusicEnabled && AppContext.current.getBackgroundMusic();
        NotificationManager.initialize();

        GameManager._app = app;

        if (ModuleManager.connectModules()) {
            if (FreeRoundBonus.wasInitialized) {
                GameManager.init();
            } else {
                setTimeout(() => {
                    GameManager.init();
                }, 3000);
            }
        } else {
            if (DEBUG) {
                GameManager.log("ERROR cant connect channel and game modules");
            }
        }
    }

    static showGame(): void {
        if (DEBUG) {
            GameManager.log("showGame");
        }

        var loading: HTMLElement = document.getElementById("loading");
        var canvas: HTMLElement = document.getElementById("canvas");

        if (loading != null && canvas != null) {
            Scaling.onLoadComplete();
            Jackpot.show();
            RetentionPanel.show();
            Paytable.openAtStart();

            //showGame is called after prepare new game and showFreeRounds, so if we have game then close frb
            if (DataManager.GameRoundClosed && Jackpot.hasMiniGame() && !FreeRoundBonus.inFrb) {
                FreeRoundBonus.disableUi();
            }

            DataManager.cleanUpElements();
            loading.style.display = "none";
            canvas.style.display = "block";
            DataManager.updateDataElements();
        } else {
            if (DEBUG) {
                GameManager.log("ERROR loading or canvas doesnt exist");
            }
        }
    }

    static setupGame(response: InitResponse): void {
        if (DEBUG) {
            GameManager.log("setupGame");
        }

        GameManager.parseResponses(response);
        DataManager.updateResponseData(true);
        ModuleManager.initModules();
        DataManager.SimilarGames = new SimilarGames();
        DataManager.SimilarGames.initialize();
        FreeRoundBonus.showFreeRounds();
    }

    static init(): void {
        if (DEBUG) {
            GameManager.log("init");
        }

        if (GameManager.requestSend) {
            return;
        } else {
            GameManager.requestSend = true;
        }

        DataManager.getServerTime();
        DataManager.controlAutoplay();
        FreeRoundBonus.initializeFreeRounds();
        Jackpot.initialize();
        GameManager._gameClient.init().then((response: InitResponse) => {
            GameManager.initReceived(response);
        });
    }

    static softInit(): void {
        if (DEBUG) {
            GameManager.log("soft init");
        }

        if (GameManager.requestSend) {
            return;
        } else {
            GameManager.requestSend = true;
        }

        DataManager.controlAutoplay();
        GameManager._gameClient.init().then((response: InitResponse) => {
            GameManager.softInitReceived(response);
        });
    }

    static collect(module: string): void {
        if (DEBUG) {
            GameManager.log("collect module: " + module);
        }

        if (GameManager.requestSend) {
            return;
        } else {
            GameManager.requestSend = true;
        }

        if (!AppContext.gameDefinition.Gamble) {
            DataManager.Collectable = true;
        }

        GameManager.canExit();

        this._collectModule = module;

        DataManager.controlAutoplay();
        if (!DataManager.GameRoundClosed && GameManager.gameResponse.totalWin > 0) {
            GameManager.gameClient.collect().then(GameManager.collectReceived);
        }
    }

    static initReceived(response: InitResponse): void {
        if (DEBUG) {
            GameManager.log("initReceived");
        }

        if (Utils.isNullOrUndefined(response)) {
            if (DEBUG) {
                GameManager.log("initReceived NULL response (error in send?)");
            }
            return;
        }

        DataManager.showTime();
        if (!Utils.isNullOrUndefined(AppContext.gameConfiguration.GUIShowSessionTimeStartValue)) {
            DataManager.showSessionTime(AppContext.gameConfiguration.GUIShowSessionTimeStartValue);
        } else {
            DataManager.showSessionTime(0);
        }

        MessageManager.postpone(GameManager.continueInitReceived, response);
    }

    static continueInitReceived(response: InitResponse): void {
        GameManager.setupGame(response);
        GameManager.showGame();

        Platforms.Platforms.gameDataLoaded({
            PlayerTokenId: AppContext.gameConfiguration.PlayerTokenId,
            HomeUrl: AppContext.gameConfiguration.ExitUrl,
            ClientToken: AppContext.gameConfiguration.ClientToken,
            GameName: GameApp.key,
            SessionData: {
                SessionBalance: AppContext.gameConfiguration.SessionBalance,
                TotalWin: AppContext.gameConfiguration.TotalWin,
                TotalBet: AppContext.gameConfiguration.TotalBet,
            },
            CurrencyCode: response.initData.currency != null ? response.initData.currency : "",
            Balance: GameManager.gameResponse.balance.real,
            Bet: BetManager.Bet,
        });

        Platforms.Platforms.rendererType(StageController.Renderer instanceof PIXI.CanvasRenderer ? "canvas" : "webGL");
        DataManager.gameState("GameLoaded");

        GameManager.requestSend = false;

        // Collect total win in last cumulative FRB round when GR isn't yet closed
        if (FreeRoundBonus.isLastFrb && FreeRoundBonus.winPayMethod === "cumulative") {
            if (!DataManager.GameRoundClosed && GameManager.gameResponse.totalWin > 0) {
                // Collect only when freespins is not enabled/active
                if (
                    !ModuleManager.freespinsEnabled() &&
                    !ModuleManager.freespinsActive() &&
                    !ModuleManager.pickEnabled() &&
                    !ModuleManager.pickActive()
                ) {
                    ModuleManager.collect(GameModules.Base);
                }
            }
        }
    }

    static softInitReceived(response: InitResponse): void {
        DataManager.updateDataElement(GameData.DataTotalWin, "0");
        DataManager.updateGameDataFromModule(GameData.DataTotalWin, "0");
        LimitDialog.closeDialog();

        GameManager.parseResponses(response);
        DataManager.updateResponseData(true);
        GameManager.requestSend = false;
        ModuleManager.restoreGameRound(GameModules.Base);
        FreeRoundBonus.openOther();
    }

    static collectReceived(response: GameResponse): void {
        if (DEBUG) {
            GameManager.log("collectReceived");
        }

        MessageManager.postpone(GameManager.continueCollectReceived, response);
    }

    static continueCollectReceived(response: GameResponse): void {
        if (!Utils.isNullOrUndefined(response)) {
            GameManager.parseResponses(response);
            DataManager.updateResponseData();
            ModuleManager.collectInModule(GameManager._collectModule);
            GameManager.canExit();
        }

        GameManager.requestSend = false;
    }

    static errorReceived(apiError: ApiClientCommunication.Types.ApiClientError): void {
        let requestName: string = (apiError.requestInfo.request as any).constructor.name;
        if (requestName === "FreeRoundsOffersRequest") {
            FreeRoundBonus.wasInitialized = true;
            return;
        }

        if (DEBUG) {
            GameManager.log("errorReceived");
        }

        // mute sounds and remove focus handling
        window.onblur = null;
        window.onfocus = null;
        PIXI.sound.muteAll();

        let localizedText = "";
        let isError = true;
        let code = apiError.httpStatusCode.toString();
        let customData: DialogOpenData = {
            special: false,
            gameRoundId: Utils.valueOrDefault(DataManager.getDataById("ROUND_ID"), "0"),
            errorId: Utils.isNullOrUndefined(apiError.error) ? "" : apiError.error.errorId,
            actions: Utils.isNullOrUndefined(apiError.error) ? [] : apiError.error["actions"], //ToDo Dostat to do ApiClientCommunication.Types.ApiClientError
        };
        let ignoreShowDialog = false;
        let hasMessages = false;
        let callbackFnc: ((arg: any) => void) | null = null;
        let isFatalError = true;

        if (apiError.error) {
            let resource = "ERROR_" + apiError.httpStatusCode + "_" + apiError.error.code;
            code = [apiError.httpStatusCode.toString(), apiError.error.code].join("-");
            localizedText = Languages.getRid(resource);

            hasMessages = apiError.error.messages != null;

            if (localizedText == null) {
                resource = "ERROR_" + apiError.httpStatusCode;
                code = apiError.httpStatusCode.toString();
                localizedText = Languages.getRid(resource);
            }

            // errors displayed as info messages with "Reload" button
            if (
                (apiError.httpStatusCode === 400 && apiError.error.code === 1009) ||
                (apiError.httpStatusCode === 400 && apiError.error.code === 1011) ||
                (apiError.httpStatusCode === 400 && apiError.error.code === 1012) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2005) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2013) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2014) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2016) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2017) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2018)
            ) {
                customData.special = true;
                customData.errorId = "";
                isError = false;
            }

            // errors displayed as info messages with "Close" button
            if (
                (apiError.httpStatusCode === 500 && apiError.error.code === 2001) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2002) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2012)
            ) {
                (<any>customData).ultraSpecial = true;
            }

            // errors with server message shown instead of resource message
            if (
                (apiError.httpStatusCode === 500 && apiError.error.code === 2015) ||
                (apiError.httpStatusCode === 500 && apiError.error.code === 2016)
            ) {
                code = "";
                localizedText = apiError.error.message;
            }

            // extend FRB error message with "collect partial" msg
            if (apiError.httpStatusCode === 500 && apiError.error.code === 2017) {
                //expired FRB offer
                if (FreeRoundBonus.collectPartial) {
                    localizedText += Languages.getRid("FRB_COLLECT_PARTIAL_ALLOWED");
                } else {
                    localizedText += Languages.getRid("FRB_COLLECT_PARTIAL_NOT_ALLOWED");
                }
            }

            // extend FRB error message with "collect partial not allowed" msg
            if (apiError.httpStatusCode === 500 && apiError.error.code === 2018) {
                //canceled FRB offer
                localizedText += Languages.getRid("FRB_COLLECT_PARTIAL_NOT_ALLOWED");
            }

            if (apiError.httpStatusCode === 500 && apiError.error.code === 2019) {
                //playtech ignored error but messages are allowed
                if (!hasMessages) {
                    ignoreShowDialog = true;
                }
                callbackFnc = MessageManager.callbackFnc;
                isFatalError = false;
            }

            if (
                (apiError.httpStatusCode === 400 && apiError.error.code === 1011) ||
                (apiError.httpStatusCode === 400 && apiError.error.code === 1012)
            ) {
                let spbSufix = AppContext.gameConfiguration.UseJackpotLocalization ? "_J" : "";
                localizedText = Languages.getRid(resource + spbSufix);
            }

            if (apiError.httpStatusCode === 500 && apiError.error.code === 2020) {
                //playtech session timer
                SessionTimer.postpone(SessionTimer.callbackFnc, SessionTimer.prepareData(apiError.error));
                return;
            }
        } else {
            // errors displayed as info messages with "Close" button
            if (apiError.httpStatusCode === 401) {
                (<any>customData).ultraSpecial = true;
            }

            let resource = "ERROR_" + apiError.httpStatusCode.toString();
            localizedText = Languages.getRid(resource);
        }

        if (DEBUG) {
            Logger.debugGroup(
                "GameManager",
                `[errorReceived] calling ` +
                    `AppContext.current.showDialog('${localizedText}', ${isError}, '${code}', 
                    ${customData.errorId}, ${customData.gameRoundId}, ${JSON.stringify(customData)})`,
            );
        }

        if (isError) {
            let error_code_for_platforms = apiError.httpStatusCode.toString();
            if (apiError.error) {
                error_code_for_platforms = [apiError.httpStatusCode.toString(), apiError.error.code].join("-");
            }
            Platforms.Platforms.error(error_code_for_platforms, localizedText);
            if (isFatalError) {
                Jackpot.showError();
                NotificationManager.disconnect();
            }
        }

        //if platform shows dialog itself, skip showing error dialog in our game window
        if (Platforms.Platforms.handleShowErrorDialog || ignoreShowDialog) {
            return;
        }

        if (hasMessages) {
            MessageManager.postpone(callbackFnc, apiError.error);
        } else {
            AppContext.current.showDialog(
                localizedText,
                isError,
                code,
                customData.errorId,
                customData.gameRoundId,
                customData,
            );
        }
    }

    static parseResponses(response: GameResponse): void {
        if (DEBUG) {
            GameManager.log("parseResponses");
        }

        if (!Utils.isNullOrUndefined(response)) {
            GameManager.gameResponse = response;
            if (response.module<GameModuleBaseResponse>(GameModules.Base) != null) {
                GameManager.baseResponse = response.module<GameModuleBaseResponse>(GameModules.Base);
            } else {
                GameManager.baseResponse = null;
            }

            if (response.module<GameModuleGambleResponse>(GameModules.Gamble) != null) {
                GameManager.gambleResponse = response.module<GameModuleGambleResponse>(GameModules.Gamble);
            } else {
                GameManager.gambleResponse = null;
            }

            if (response.module<GameModuleFreespinResponse>(GameModules.Freespins) != null) {
                GameManager.freespinsResponse = response.module<GameModuleFreespinResponse>(GameModules.Freespins);
            } else {
                GameManager.freespinsResponse = null;
            }

            if (response.module<GameModulePickResponse>(GameModules.Pick) != null) {
                GameManager.pickResponse = response.module<GameModulePickResponse>(GameModules.Pick);
            } else {
                GameManager.pickResponse = null;
            }
        }
    }

    static collectable(): boolean {
        if (ModuleManager.gambleActive()) {
            return false;
        }

        if (FreeRoundBonus.endMsgDisplayed) {
            return false;
        }

        if (DataManager.GameRoundClosed && Jackpot.hasMiniGame()) {
            return false;
        }

        if (DataManager.GameRoundClosed && DataManager.Collectable) {
            return true;
        }

        return false;
    }

    static canExit(exit: boolean = false): boolean {
        let canExit = GameManager.collectable();

        if (DEBUG) {
            console.log(`GameManager.canExit(exit: ${exit}) -> afterCollect: ${canExit}`);
        }

        if (
            canExit &&
            !exit &&
            ResponsibleDialog1.showResponsibleDialog1OnCollect &&
            ResponsibleDialog1.responsibleDialog1Data != null &&
            !FreeRoundBonus.isLastFrb
        ) {
            DataManager.Autoplay = false;
            DataManager.AutoplayAfterGamble = false;
            ResponsibleDialog1.openDialog(ResponsibleDialog1.responsibleDialog1Data);
            ResponsibleDialog1.showResponsibleDialog1OnCollect = false;
            ResponsibleDialog1.responsibleDialog1Data = null;
            ModuleManager.disableBase();
            ModuleManager.disableGamble();
            ModuleManager.resetWinAnimator(GameModules.Base);
        }

        if (GameManager.exitOnCollect && canExit) {
            if (DEBUG) {
                console.log(
                    `GameManager.canExit(exit: ${exit}) -> manuall call of GameApp.exit() ` +
                        `because of GameManager.exitOnCollect = ${GameManager.exitOnCollect}`,
                );
            }
            GameApp.exit();
            return false;
        }
        if (canExit && exit) {
            return canExit;
        } else {
            if (!GameManager.exitOnCollect && exit) {
                if (DEBUG) {
                    console.log(
                        `GameManager.canExit(exit: ${exit}) -> ` +
                            `changing GameManager.exitOnCollect(${GameManager.exitOnCollect}) to value '${exit}'`,
                    );
                }

                GameManager.exitOnCollect = true; //exit;
            }
        }

        if (DEBUG) {
            console.log(`GameManager.canExit(exit: ${exit}) -> returns: ${canExit}`);
        }

        return canExit;
    }

    static showResponsibleDialog1(data: ClientHost.Types.ClientHostResponsibleDialogData): void {
        if (GameManager.canExit()) {
            DataManager.Autoplay = false;
            DataManager.AutoplayAfterGamble = false;
            ResponsibleDialog1.openDialog(data);
            ResponsibleDialog1.showResponsibleDialog1OnCollect = true;
            ResponsibleDialog1.responsibleDialog1Data = null;
            ModuleManager.disableBase();
            ModuleManager.disableGamble();
            ModuleManager.resetWinAnimator(GameModules.Base);
        } else {
            ResponsibleDialog1.showResponsibleDialog1OnCollect = true;
            ResponsibleDialog1.responsibleDialog1Data = data;
        }
    }

    static disablePlay(): void {
        GameManager.disableOnCollect = true;
        if (GameManager.collectable()) {
            GameManager.disableGamePlay = true;
            ModuleManager.disableBase();
            DataManager.gameState("GamePlayDisabled");
        }
    }

    static enablePlay(): void {
        GameManager.disableOnCollect = false;
        GameManager.disableGamePlay = false;
        if (!GameManager.dontEnableGamePlay) {
            ModuleManager.restoreGameRound(GameModules.Base);
        }
        DataManager.gameState("GamePlayEnabled");
    }

    static openConfirmationDialog(module: string, text: string): void {
        const dialogData = {
            text: text,
            actions: [
                {
                    type: "restore",
                    text: Languages.getRid("CONTINUE"),
                    customData: module,
                },
            ],
            special: true,
        };
        ModuleManager.disableChannelModule(module);
        AppContext.current.showDialog(text, false, null, null, null, dialogData);
    }

    static closeResponsibleDialog1(): void {
        GameManager.requestSend = false;
        ResponsibleDialog1.showResponsibleDialog1OnCollect = false;
        ModuleManager.restoreGameRound(GameModules.Base);
    }

    private static log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("GameManager", s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Managers/GameManager.ts
 **/